﻿import { initializeMap } from "./locationsMap";

export const locationFinder = () => {
  /** Wires up accordion behavior for category filters */
  const setupFilterAccordion = () => {
    const filterSections = document.querySelectorAll("#filters section");

    const expandedSectionsElement = document.querySelector(
      "#expanded-facets"
    ) as HTMLInputElement;

    const collapsedUlClassName = "max-h-0";

    const toggleSection = (
      sectionElement: HTMLElement,
      visible?: boolean | undefined
    ) => {
      const ul = sectionElement.querySelector("ul") as HTMLElement;

      const collapsedIcon = sectionElement.querySelector(
        "svg[data-icon=collapsed]"
      ) as HTMLElement;

      const expandedIcon = sectionElement.querySelector(
        "svg[data-icon=expanded]"
      ) as HTMLElement;

      // Toggle list visibility if needed
      if (
        (visible === true && ul.classList.contains(collapsedUlClassName)) ||
        (visible === false && !ul.classList.contains(collapsedUlClassName)) ||
        visible === undefined
      ) {
        ul.classList.toggle(collapsedUlClassName);

        if (ul.classList.contains(collapsedUlClassName)) {
          expandedIcon.classList.add("hidden");
          collapsedIcon.classList.remove("hidden");
        } else {
          collapsedIcon.classList.add("hidden");
          expandedIcon.classList.remove("hidden");
        }
      }
    };

    const refreshExpandedSectionsValue = (expandSectionsWithFilters: boolean) => {
      expandedSectionsElement.value = "";

      const expandedSections: string[] = [];

      filterSections.forEach(section => {
        const sectionElement = section as HTMLElement;

        const isCollapsed = !!sectionElement.querySelector(`ul.${collapsedUlClassName}`);

        const hasCheckedFilters =
          !!sectionElement.querySelectorAll(`input[checked]`).length;

        // Also consider expanded if there is at least one checked filter
        if ((!isCollapsed || hasCheckedFilters) && sectionElement.dataset.facets) {
          expandedSections.push(sectionElement.dataset.facets);

          if (expandSectionsWithFilters) {
            toggleSection(sectionElement, true);
          }
        }
      });

      expandedSectionsElement.value = expandedSections.join(",");
    };

    filterSections.forEach(section => {
      // Show/hide filters group
      section.addEventListener("click", _ => {
        toggleSection(section as HTMLElement);

        refreshExpandedSectionsValue(false);
      });
    });

    refreshExpandedSectionsValue(true);
  };

  /** Wires up event to post form whenever values (e.g. filters) change. */
  const wireUpFormInputElements = () => {
    const form = document.getElementById("location-finder-form") as HTMLFormElement;

    form.querySelectorAll<HTMLInputElement>("input").forEach(elm => {
      elm.addEventListener("change", _ => {
        if (elm.disabled) {
          // Ignore changes to disabled elements
          return;
        }

        refreshForm(elm.closest("form") as HTMLFormElement);
      });
    });
  };

  /** Wires up event to post form whenever selected item (e.g. sort order) changes. */
  const wireUpSelectElements = () => {
    const form = document.getElementById("location-finder-form") as HTMLFormElement;

    form.querySelectorAll<HTMLSelectElement>("select").forEach(elm => {
      elm.addEventListener("change", _ => {
        if (elm.disabled) {
          // Ignore changes to disabled elements
          return;
        }

        refreshForm(elm.closest("form") as HTMLFormElement);
      });
    });
  };

  /** Posts a form asynchronously and returns the response HTML. */
  const postForm = (form: HTMLFormElement) =>
    new Promise<string>((resolve, reject) => {
      fetch(form.action, {
        method: "POST",
        body: new FormData(form),
      })
        .then(response => response.text())
        .then(html => {
          resolve(html);
        })
        .catch(error => {
          reject(error);
        });
    });

  /** Updates query string to match current filters */
  const updateUrl = () => {
    const params = new URLSearchParams(window.location.search);

    // Update query string to match current filters
    const locationFinderWrapper = document.getElementById(
      "location-finder-wrapper"
    ) as HTMLElement;

    const filters = locationFinderWrapper.dataset["filters"] || "";
    const orderby = locationFinderWrapper.dataset["orderby"] || "";
    const keywords = locationFinderWrapper.dataset["normalizedKeywords"] || "";

    let hasParameters = false;

    const setQueryString = (name: string, value: string | null) => {
      if (value) {
        // We don't use encodeURIComponent primarily for aesthetic reasons,
        // instead we explicitly replace expected characters
        value = value.replace("&", "%26");

        params.set(name, value);
        hasParameters = true;
      } else {
        params.delete(name);
      }
    };

    setQueryString("filters", filters);
    setQueryString("orderby", orderby);
    setQueryString("keywords", keywords);

    const queryString = hasParameters ? `?${decodeURIComponent(params.toString())}` : "";

    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${queryString}`;

    window.history.replaceState({}, "", newUrl);
  };

  /** Posts the current form and expects HTML to be returned for updating the page. */
  const refreshForm = (form: HTMLFormElement) => {
    postForm(form).then(
      html => {
        const container = document.getElementById(
          "location-finder-wrapper"
        ) as HTMLElement;

        container.outerHTML = html;

        updateUrl();

        initialize(true);
      },
      error => {
        console.error("Failed to refresh locations", error);
      }
    );
  };

  /** Sets the visibility of the filters panel (applicable for tablet and mobile). */
  const showHideFilterPanel = (show: boolean | null) => {
    const filtersContainer = document.getElementById("filters") as HTMLDivElement;

    if (show === null) {
      show = !filtersContainer.classList.contains("!translate-x-0");
    }

    const overlay = document.getElementById(
      "location-finder-wrapper-overlay"
    ) as HTMLDivElement;

    const filtersExpanded = document.getElementById(
      "filtersExpanded"
    ) as HTMLInputElement;

    // Set checkbox to indicate whether panel is open
    filtersExpanded.checked = show;

    // Toggle visibility of filters container
    if (show) {
      filtersContainer.classList.add("!translate-x-0");

      overlay.classList.remove("pointer-events-none");
      overlay.classList.add("!opacity-100");
    } else {
      filtersContainer.classList.remove("!translate-x-0");

      overlay.classList.add("pointer-events-none");
      overlay.classList.remove("!opacity-100");
    }
  };

  /** Shows/hides the filters panel (in mobile and tablet) when button is clicked. */
  const wireUpFiltersButton = () => {
    const filtersButton = document.getElementById(
      "show-hide-filters-button"
    ) as HTMLButtonElement;

    filtersButton.addEventListener("click", _ => {
      showHideFilterPanel(null);
    });
  };

  /** Resets all filters */
  const clearAllFilters = () => {
    const form = document.getElementById("location-finder-form") as HTMLFormElement;

    form.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach(elm => {
      elm.checked = false;
    });

    form.querySelectorAll<HTMLSelectElement>("select").forEach(elm => {
      elm.value = "";
    });

    form.querySelectorAll<HTMLInputElement>("input[type=text]").forEach(elm => {
      elm.value = "";
    });

    refreshForm(form);
  };

  /** Configures search textboxes (we have one for mobile and one for tablet/desktop) */
  const configureSearchBoxes = () => {
    const searchTextboxes = document.querySelectorAll<HTMLInputElement>(
      "[data-search-type=keywords]"
    );

    searchTextboxes.forEach(element => {
      // Trigger change on keywords textbox when Enter is pressed
      element.addEventListener("keydown", event => {
        if (event.key === "Enter") {
          (event.target as HTMLInputElement).blur(); // To trigger change, if we trigger the actual 'change' event we get double posts
        }
      });

      // Since we have more than one search textbox, we reset the other one when one changes
      element.addEventListener("change", event => {
        searchTextboxes.forEach(textbox => {
          if (textbox.id != (event.target as HTMLElement).id) {
            textbox.value = "";
          }
        });
      });

      // Reset search box value when X is clicked
      (
        element.parentElement?.querySelector(
          "svg[data-search-function=clear]"
        ) as HTMLElement
      ).addEventListener("click", event => {
        element.value = "";

        // Return to search icon
        const clearIcon = event.target as HTMLElement;

        clearIcon.classList.add("hidden");

        const searchIcon = element.parentElement?.querySelector(
          "svg:not([data-search-function=clear])"
        );

        searchIcon?.classList.remove("hidden");

        // Make the change take effect
        element.parentElement?.querySelector("input")?.dispatchEvent(new Event("change"));
      });
    });
  };

  const configureForm = () => {
    // Prevent POST of form, as we'll do it async (AJAX)
    const form = document.getElementById("location-finder-form") as HTMLFormElement;

    form.onsubmit = event => {
      event.preventDefault();
    };

    // Prevent form from being submitted by hitting Enter
    form.addEventListener("keydown", event => {
      if (event.key === "Enter") {
        event.preventDefault();
      }
    });
  };

  /** Sets up behaviors after page load/refresh.
   * @param ajaxRequst Specifies if this is an AJAX request or not, which affects how user consent is determined, since Usercentrics is already loaded on Ajax requests, so its events won't trigger
   */
  const initialize = (ajaxRequest: boolean) => {
    configureSearchBoxes();

    wireUpFormInputElements();

    wireUpSelectElements();

    wireUpFiltersButton();

    configureForm();

    setupFilterAccordion();

    // Hide filters panel on Esc (applicable for tablet and mobile)
    document.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        showHideFilterPanel(false);
      }
    });

    // Hide filters panel if clicking outside (i.e. on the overlay)
    document
      .getElementById("location-finder-wrapper-overlay")
      ?.addEventListener("click", _ => {
        showHideFilterPanel(false);
      });

    // Hide filters button if "Show locations" button is clicked
    (
      document.getElementById("location-finder-filters-panel-button") as HTMLButtonElement
    ).addEventListener("click", _ => {
      showHideFilterPanel(false);
    });

    // Clear filters on click
    (
      document.getElementById("location-finder-clear-filters-button") as HTMLElement
    ).addEventListener("click", clearAllFilters);

    initializeMap(ajaxRequest);
  };

  initialize(false); // Initial page load
};
